import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Card } from "@/shared/components/ui/Card"
import {
  FormField,
  FormSection,
  NumericInput,
  TextAreaField,
  DateTimeField,
  ImageUploadField,
  AppetiteRating,
} from "@/shared/components/forms"
import { feedRecordSchema, type FeedRecordFormData } from "../schemas/feedRecordSchema"

interface FeedRecordFormProps {
  defaultValues?: Partial<FeedRecordFormData>
  onSubmit: (data: FeedRecordFormData) => Promise<void>
  isLoading: boolean
}

/**
 * Form for creating/editing feed records.
 */
export function FeedRecordForm({ defaultValues, onSubmit, isLoading }: FeedRecordFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FeedRecordFormData>({
    resolver: zodResolver(feedRecordSchema),
    defaultValues: {
      feedType: "",
      feedBrand: "",
      totalFeed: undefined,
      maximumFeed: undefined,
      appetite: undefined,
      feedTime: new Date(),
      notes: "",
      imageIds: [],
      ...defaultValues,
    },
  })

  const notes = watch("notes") || ""

  // Count errors in each section for badges
  const feedDetailsErrors = [
    errors.feedType,
    errors.feedBrand,
    errors.totalFeed,
    errors.maximumFeed,
    errors.feedTime,
  ].filter(Boolean).length
  const appetiteErrors = errors.appetite ? 1 : 0
  const notesErrors = [errors.notes, errors.imageIds].filter(Boolean).length

  const onFormSubmit = handleSubmit(async (data) => {
    await onSubmit(data)
  })

  return (
    <form id="feed-record-form" onSubmit={onFormSubmit} className="space-y-4">
      {/* Feed Details - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Feed Details" defaultExpanded={true} errorCount={feedDetailsErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Feed Type" required error={errors.feedType}>
              <input
                type="text"
                {...register("feedType")}
                placeholder="e.g., Pellet, Flake, etc."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.feedType
                    ? "border-red-300 focus:ring-red-500/20 focus:border-red-500"
                    : "border-slate-200/50 focus:ring-emerald-500/20 focus:border-emerald-500"
                }`}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Feed Brand" required error={errors.feedBrand}>
              <input
                type="text"
                {...register("feedBrand")}
                placeholder="e.g., Cargill, CP, etc."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.feedBrand
                    ? "border-red-300 focus:ring-red-500/20 focus:border-red-500"
                    : "border-slate-200/50 focus:ring-emerald-500/20 focus:border-emerald-500"
                }`}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Total Feed" required error={errors.totalFeed} hint="Amount actually fed">
              <NumericInput
                {...register("totalFeed", { valueAsNumber: true })}
                unit="kg"
                placeholder="0.0"
                hasError={!!errors.totalFeed}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Maximum Feed" required error={errors.maximumFeed} hint="Recommended daily amount">
              <NumericInput
                {...register("maximumFeed", { valueAsNumber: true })}
                unit="kg"
                placeholder="0.0"
                hasError={!!errors.maximumFeed}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Feeding Date & Time" required error={errors.feedTime} className="sm:col-span-2">
              <DateTimeField
                name="feedTime"
                control={control}
                showTime={true}
                maxDate={new Date()}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Appetite Rating - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Appetite Assessment" defaultExpanded={true} errorCount={appetiteErrors}>
          <FormField label="Appetite Rating" required error={errors.appetite}>
            <Controller
              name="appetite"
              control={control}
              render={({ field }) => (
                <AppetiteRating
                  value={field.value || 0}
                  onChange={field.onChange}
                  disabled={isLoading}
                  hasError={!!errors.appetite}
                />
              )}
            />
          </FormField>
        </FormSection>
      </Card>

      {/* Notes & Images - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Notes & Images" defaultExpanded={false} errorCount={notesErrors}>
          <div className="space-y-4">
            <FormField label="Notes" error={errors.notes} hint="Optional observations">
              <TextAreaField
                {...register("notes")}
                placeholder="Add any feeding observations, fish behavior, or weather notes..."
                maxLength={2000}
                currentLength={notes.length}
                rows={4}
                hasError={!!errors.notes}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Images" error={errors.imageIds} hint="Up to 5 images, max 10MB each">
              <Controller
                name="imageIds"
                control={control}
                render={({ field }) => (
                  <ImageUploadField
                    value={field.value || []}
                    onChange={field.onChange}
                    maxFiles={5}
                    disabled={isLoading}
                  />
                )}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>
    </form>
  )
}
